import type { RouteRecordRaw } from 'vue-router'
import {
  OldEmailLookupRoutes,
  OldFormsRoutes,
  OldCarrierLookupRoutes,
  OldPointAiRoutes,
  OldSmsSurveyRoutes,
} from '@/routes/user/services/types'
import { FormsRoutes } from '@/routes/user/forms/types'
import { CarrierLookupRoutes, EmailLookupRoutes } from '@/routes/user/lookup/types'
import { UserSettingsRoutes } from '@/routes/user/settings'
import { getOldRouteWithRedirect } from '@/utils/oldRouteRedirect'
import PassThrough from '@/components/layout/PassThrough.vue'
import { SmsSurveyRoutes } from '@/routes/user/sms-survey/types'
import { ChatSettingsAliasedRoutes } from '@/routes/types'

export const getCarrierLookupRoutes = (): RouteRecordRaw[] => [
  {
    name: OldCarrierLookupRoutes.index,
    path: 'carrier-lookup',
    redirect: { name: OldCarrierLookupRoutes.singleLookup },
    children: [
      getOldRouteWithRedirect(OldCarrierLookupRoutes.singleLookup, '', CarrierLookupRoutes.singleLookup),
      getOldRouteWithRedirect(OldCarrierLookupRoutes.bulkLookup, 'bulk-lookup', CarrierLookupRoutes.bulkLookup),
      getOldRouteWithRedirect(OldCarrierLookupRoutes.history, 'history', CarrierLookupRoutes.history),
      getOldRouteWithRedirect(OldCarrierLookupRoutes.flow, 'bulk-carrier-lookup', CarrierLookupRoutes.flow),
    ],
  },
]

export const getEmailLookupRoutes = (): RouteRecordRaw[] => [
  {
    name: OldEmailLookupRoutes.index,
    path: 'email-lookup',
    redirect: { name: OldEmailLookupRoutes.singleLookup },
    children: [
      getOldRouteWithRedirect(OldEmailLookupRoutes.singleLookup, '', EmailLookupRoutes.singleLookup),
      getOldRouteWithRedirect(OldEmailLookupRoutes.bulkLookup, 'bulk-lookup', EmailLookupRoutes.bulkLookup),
      getOldRouteWithRedirect(OldEmailLookupRoutes.history, 'history', EmailLookupRoutes.history),
      getOldRouteWithRedirect(OldEmailLookupRoutes.flow, 'bulk-email-lookup', EmailLookupRoutes.flow),
    ],
  },
]

export const getFormsRoutes = (): RouteRecordRaw[] => [
  {
    name: OldFormsRoutes.index,
    path: 'forms',
    redirect: { name: OldFormsRoutes.subscribe },
    children: [
      getOldRouteWithRedirect(OldFormsRoutes.subscribe, 'subscribe', FormsRoutes.subscribe),
      getOldRouteWithRedirect(OldFormsRoutes.clickToText, 'click-to-text', FormsRoutes.clickToText),
    ],
  },
]

export const getFormsDetailsRoutes = (): RouteRecordRaw[] => [
  getOldRouteWithRedirect(OldFormsRoutes.newClickToText, 'forms/click-to-text/new', FormsRoutes.createClickToText),
  getOldRouteWithRedirect(OldFormsRoutes.editClickToText, 'forms/click-to-text/:formId', FormsRoutes.editClickToText),
  getOldRouteWithRedirect(OldFormsRoutes.editSubscribe, 'forms/subscribe/:formId', FormsRoutes.editSubscribe),
]

export const getAutomationRulesRoutes = (): RouteRecordRaw[] => [
  {
    name: 'user.services.automationRules',
    path: 'automation-rules',
    redirect: { name: 'user.services.automationRules.rules' },
    children: [
      getOldRouteWithRedirect('user.services.automationRules.rules', '', UserSettingsRoutes.AutoResponders),
      getOldRouteWithRedirect(
        'user.services.automationRules.keywords',
        'keywords',
        UserSettingsRoutes.AutoRespondersKeywords,
      ),
      getOldRouteWithRedirect('user.services.automationRules.logs', 'logs', UserSettingsRoutes.AutoRespondersLogs),
      getOldRouteWithRedirect('user.services.automationRules.newRule', 'new', UserSettingsRoutes.AutoRespondersNewRule),
      getOldRouteWithRedirect(
        'user.services.automationRules.editRule',
        ':ruleId',
        UserSettingsRoutes.AutoRespondersEditRule,
      ),
      getOldRouteWithRedirect(
        'user.services.automationRules.logsDetails',
        'logs/:automationRuleLogId',
        UserSettingsRoutes.AutoRespondersLogsDetails,
      ),
      getOldRouteWithRedirect(
        'user.services.automationRules.details',
        'details/:ruleId',
        UserSettingsRoutes.AutoRespondersDetails,
      ),
    ],
  },
]

export const getApiRoutes = (): RouteRecordRaw[] => [
  {
    name: 'user.services.api',
    path: 'api',
    redirect: { name: 'user.services.api.apiv2Keys' },
    children: [
      getOldRouteWithRedirect('user.services.api.apiv2Keys', '', UserSettingsRoutes.ApiV2Keys),
      getOldRouteWithRedirect('user.services.api.apiv2Settings', 'api-v2-settings', UserSettingsRoutes.Apiv2Settings),
      getOldRouteWithRedirect('user.services.api.integrations', 'integrations', UserSettingsRoutes.Integrations),
      getOldRouteWithRedirect('user.services.api.apiv1', 'api-v1', UserSettingsRoutes.ApiV1),
    ],
  },
]

export const getPointAIRoutes = (): RouteRecordRaw[] => [
  {
    name: OldPointAiRoutes.root,
    path: 'point-ai',
    component: PassThrough,
    redirect: { name: OldPointAiRoutes.websites },
    children: [
      {
        name: OldPointAiRoutes.main,
        path: '',
        redirect: { name: OldPointAiRoutes.websites },
        children: [
          getOldRouteWithRedirect(OldPointAiRoutes.websites, 'websites', UserSettingsRoutes.PointAiWebsites),
          getOldRouteWithRedirect(OldPointAiRoutes.documents, 'documents', UserSettingsRoutes.PointAiDocuments),
        ],
      },
      getOldRouteWithRedirect(
        OldPointAiRoutes.websitesDownloaded,
        'websites/:id',
        UserSettingsRoutes.PointAiWebsitesDownloaded,
      ),
    ],
  },
]

export const getEmailToSmsRoutes = (): RouteRecordRaw[] => [
  {
    name: 'user.services.emailToSms',
    path: 'email-to-sms',
    redirect: { name: 'user.services.emailToSms.allowedEmails' },
    children: [
      getOldRouteWithRedirect('user.services.emailToSms.allowedEmails', '', UserSettingsRoutes.EmailToSmsAllowedEmails),
      getOldRouteWithRedirect(
        'user.services.emailToSms.sendReceiveSettings',
        'send-and-receive-settings',
        UserSettingsRoutes.EmailToSmsSendReceiveSettings,
      ),
      getOldRouteWithRedirect(
        'user.services.emailToSms.distributionLists',
        'distribution-lists',
        UserSettingsRoutes.EmailToSmsDistributionLists,
      ),
    ],
  },
  getOldRouteWithRedirect(
    'user.services.emailToSms.createDistribution',
    'email-to-sms/distribution-lists/new',
    UserSettingsRoutes.EmailToSmsCreateDistribution,
  ),
  getOldRouteWithRedirect(
    'user.services.emailToSms.editDistribution',
    'email-to-sms/distribution-lists/:distributionId',
    UserSettingsRoutes.EmailToSmsEditDistribution,
  ),
]

export const getSmsSurveysRoutes = (): RouteRecordRaw[] => [
  {
    name: OldSmsSurveyRoutes.index,
    path: 'sms-surveys',
    redirect: { name: OldSmsSurveyRoutes.overview },
    children: [
      getOldRouteWithRedirect(OldSmsSurveyRoutes.overview, '', SmsSurveyRoutes.overview),
      {
        name: OldSmsSurveyRoutes.details,
        path: 'details/:smsSurveyId',
        redirect: { name: OldSmsSurveyRoutes.detailsOverview },
        children: [
          getOldRouteWithRedirect(OldSmsSurveyRoutes.detailsOverview, 'overview', SmsSurveyRoutes.detailsOverview),
          getOldRouteWithRedirect(
            OldSmsSurveyRoutes.detailsReplies,
            'replies/:questionId?',
            SmsSurveyRoutes.detailsReplies,
          ),
          getOldRouteWithRedirect(OldSmsSurveyRoutes.detailsAnalytics, 'analytics', SmsSurveyRoutes.detailsAnalytics),
        ],
      },
    ],
  },
]

export const getOldSenderSettingsRoutes = (): RouteRecordRaw[] => [
  {
    name: 'user.services.senderSettings',
    path: '',
    redirect: { name: 'user.services.senderSettings.numbers' },
    children: [
      getOldRouteWithRedirect('user.services.senderSettings.numbers', 'numbers', UserSettingsRoutes.SmsVoiceNumbers),
      getOldRouteWithRedirect(
        'user.services.senderSettings.countries',
        'countries',
        UserSettingsRoutes.SmsVoiceSenderSettings,
      ),
      getOldRouteWithRedirect('user.services.senderSettings.tenDlc', '10dlc', UserSettingsRoutes.SmsVoiceTenDlc),
      getOldRouteWithRedirect(
        'user.services.senderSettings.tollFree',
        'toll-free-verification',
        UserSettingsRoutes.SmsVoiceTollFree,
      ),
      getOldRouteWithRedirect(
        'user.services.senderSettings.senderIds',
        'sender-ids',
        UserSettingsRoutes.SmsVoiceByocSenderIds,
      ),
      getOldRouteWithRedirect(
        'user.services.senderSettings.buyNumber.number',
        'buy-number/number',
        UserSettingsRoutes.SmsVoiceBuyNumber,
      ),
      getOldRouteWithRedirect(
        'user.services.senderSettings.buyNumber.callForwarding',
        'buy-number/call-forwarding/:id',
        UserSettingsRoutes.SmsVoiceCallForwarding,
      ),
      {
        name: 'user.services.senderSettings.tenDlcCampaignDetails.overview',
        path: 'sender-settings/10dlc/campaign/:campaignId',
        alias: ChatSettingsAliasedRoutes.complianceTenDlcSenderSettings,
        redirect: { name: UserSettingsRoutes.SmsVoiceTenDlcCampaignOverview },
      },
      {
        name: 'user.services.senderSettings.tenDlcCampaignDetails.numbers',
        path: 'sender-settings/10dlc/campaign/:campaignId/numbers',
        alias: ChatSettingsAliasedRoutes.complianceTenDlcSenderSettingsNumbers,
        redirect: { name: UserSettingsRoutes.SmsVoiceTenDlcCampaignNumbers },
      },
    ],
  },
]
